/**
 * reCAPTCHA v3 helper utilities.
 *
 * We use react-google-recaptcha-v3's GoogleReCaptchaProvider in App.tsx
 * to load the script. This module provides helpers for executing and
 * verifying tokens on submission.
 *
 * Usage:
 *   import { executeRecaptcha, isRecaptchaValid } from "@/lib/recaptcha";
 *
 *   // In your submit handler (inside GoogleReCaptchaProvider):
 *   const token = await executeRecaptcha("submit_form");
 *   if (!token || !(await verifyRecaptchaToken(token))) {
 *     // reject submission
 *   }
 */

const RECAPTCHA_SCORE_THRESHOLD = 0.5;

/**
 * Execute reCAPTCHA v3 and return a token.
 * Must be called from within a GoogleReCaptchaProvider context.
 *
 * @param action - The action name (e.g., "submit_form")
 * @param executeRecaptcha - The execute function from useGoogleReCaptcha()
 */
export async function getRecaptchaToken(
  action: string,
  executeRecaptcha: ((action?: string) => Promise<string>) | undefined
): Promise<string | null> {
  if (!executeRecaptcha) {
    console.error("[reCAPTCHA] executeRecaptcha not available");
    return null;
  }

  try {
    const token = await executeRecaptcha(action);
    return token;
  } catch (error) {
    console.error("[reCAPTCHA] Failed to execute:", error);
    return null;
  }
}

/**
 * Verify a reCAPTCHA token server-side.
 * This should be called from a Vercel API route or Supabase Edge Function,
 * NOT from the client. The client sends the token; the server verifies it.
 *
 * For client-side use: send the token along with the form submission,
 * and have the server call this function.
 */
export async function verifyRecaptchaToken(
  token: string,
  secretKey: string
): Promise<{ success: boolean; score: number }> {
  const response = await fetch("https://www.google.com/recaptcha/api/siteverify", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: `secret=${secretKey}&response=${token}`,
  });

  const data = await response.json();

  return {
    success: data.success && data.score >= RECAPTCHA_SCORE_THRESHOLD,
    score: data.score ?? 0,
  };
}

export { RECAPTCHA_SCORE_THRESHOLD };
